import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CertificationCards from './components/CertificationCards';
import RequirementsSection from './components/RequirementsSection';
import ExamOverview from './components/ExamOverview';
import AwardItems from './components/AwardItems';
import UsageSection from './components/UsageSection';
import CTASection from './components/CTASection';
import FAQSection from './components/FAQSection';
import RelatedPages from './components/RelatedPages';
import Seo from '@/components/seo/Seo';

const breadcrumbData = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://butler-academy.jp/' },
    { '@type': 'ListItem', position: 2, name: 'Tuition & Benefits', item: 'https://butler-academy.jp/pricing' },
    { '@type': 'ListItem', position: 3, name: 'Completion Certificate & Certification', item: 'https://butler-academy.jp/certificate' },
  ],
};

const orgData = {
  '@context': 'https://schema.org',
  '@type': 'EducationalOrganization',
  name: 'Japan Butler Academy',
  parentOrganization: { '@type': 'Organization', name: 'Japan Butler Association' },
  address: { '@type': 'PostalAddress', addressLocality: 'Aomi 2-7-4, Koto-ku, Tokyo' },
  telephone: '03-6824-4086',
};

const credentialData = {
  '@context': 'https://schema.org',
  '@type': 'EducationalOccupationalCredential',
  name: 'Certified Butler (3 Tiers)',
  description: 'A private certification awarded by the Japan Butler Association to graduates who pass the practical examination at Japan Butler Academy. Three tiers: Certified Associate Butler, Certified Professional Butler, and Certified Master Butler.',
  credentialCategory: 'certification',
  recognizedBy: { '@type': 'Organization', name: 'Japan Butler Association' },
  educationalLevel: ['Foundation', 'Intermediate', 'Advanced'],
};

function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => { setVisible(window.scrollY > 500); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full border border-accent-400/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 z-50 cursor-pointer"
      aria-label={t('certificate.backToTop.ariaLabel')}
    >
      <i className="ri-arrow-up-line text-base"></i>
    </button>
  );
}

export default function CertificatePage() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  if (!mounted) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <Seo route="/certificate" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(credentialData) }} />

      <Header />
      <main>
        <HeroSection />

        <CertificationCards />
        <RequirementsSection />
        <ExamOverview />
        <AwardItems />
        <UsageSection />
        <CTASection />
        <FAQSection />
        <RelatedPages />

        <footer className="py-12 bg-background-100/50 text-center">
          {String(t('certificate.footer.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 tracking-[0.04em]">{__s}</p>))}
        </footer>

        <BackToTop />
      </main>
      <Footer />
    </>
  );
}
